/**
 * Hook pour gérer la santé du système
 * @module admin/hooks/useHealth
 */

import { useState, useCallback } from 'react';
import {
  getStartupHealth,
  getCurrentHealth,
  refreshHealth,
} from '../services/maintenanceService';

/**
 * Hook de gestion de la santé du système
 * @returns {Object} État et fonctions pour la santé
 */
export const useHealth = () => {
  const [healthStatus, setHealthStatus] = useState(null);
  const [startupHealth, setStartupHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [healthError, setHealthError] = useState(null);

  /**
   * Récupère le rapport de santé au démarrage
   */
  const fetchStartupHealth = useCallback(async () => {
    try {
      const data = await getStartupHealth();
      if (data.success) {
        setStartupHealth(data.data);
      }
    } catch (err) {
      console.error(
        'Erreur lors de la récupération de la santé au démarrage:',
        err
      );
    }
  }, []);

  /**
   * Récupère l'état de santé actuel
   */
  const fetchCurrentHealth = useCallback(async () => {
    setHealthLoading(true);
    setHealthError(null);

    try {
      const data = await getCurrentHealth();
      if (data.success) {
        setHealthStatus(data.data);
      }
    } catch (err) {
      setHealthError(err.message);
    } finally {
      setHealthLoading(false);
    }
  }, []);

  /**
   * Rafraîchit l'état de santé
   */
  const handleRefreshHealth = useCallback(async () => {
    setHealthLoading(true);
    setHealthError(null);

    try {
      const data = await refreshHealth();
      if (data.success) {
        setHealthStatus(data.data);
      }
    } catch (err) {
      setHealthError(err.message);
    } finally {
      setHealthLoading(false);
    }
  }, []);

  return {
    healthStatus,
    startupHealth,
    healthLoading,
    healthError,
    fetchStartupHealth,
    fetchCurrentHealth,
    handleRefreshHealth,
  };
};
